import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    rank: '',
    years_of_service: 0,
    emt_training: false,
    beach: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [beaches, setBeaches] = useState([]);
  const [beachesLoading, setBeachesLoading] = useState(true);

  const { register } = useAuth();
  const navigate = useNavigate();

  const ranks = ['Lifeguard', 'Rookie'];

  // Fetch beaches on component mount
  useEffect(() => {
    const fetchBeaches = async () => {
      try {
        const response = await apiClient.get('/api/public/beaches');
        // Filter out Bay AM, Bay PM, and Roaming beaches (public endpoint already filters for active beaches)
        const filteredBeaches = response.data.filter(beach => 
          !['Bay AM', 'Bay PM', 'Roaming'].includes(beach.name)
        );
        setBeaches(filteredBeaches);
      } catch (error) {
        console.error('Failed to fetch beaches:', error);
        setError('Failed to load beach options');
      } finally {
        setBeachesLoading(false);
      }
    };

    fetchBeaches();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!formData.username || !formData.first_name || !formData.last_name || !formData.email || !formData.password || !formData.rank || !formData.beach) {
      setError('Username, first name, last name, email, password, rank, and beach preference are required');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Enhanced password validation to match backend requirements
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordPattern.test(formData.password)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)');
      setLoading(false);
      return;
    }

    // Prepare data for submission
    const { confirmPassword, ...submitData } = formData;
    
    const result = await register(submitData);
    
    if (result.success) {
      setSuccess('Registration successful! You can now log in.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: '500px' }}>
        <h1 className="auth-title">Join {process.env.REACT_APP_BRAND_SHORT_NAME || 'LightHouse'}</h1>
        <p className="auth-subtitle">Create your account</p>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name" className="form-label">
                First Name *
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                className="form-input"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Enter first name"
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name" className="form-label">
                Last Name *
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                className="form-input"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Enter last name"
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="username" className="form-label">
                Username *
              </label>
              <input
                type="text"
                id="username"
                name="username"
                className="form-input"
                value={formData.username}
                onChange={handleChange}
                placeholder="Enter username"
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="rank" className="form-label">
                Rank *
              </label>
              <select
                id="rank"
                name="rank"
                className="form-select"
                value={formData.rank}
                onChange={handleChange}
                disabled={loading}
                required
              >
                <option value="">Select rank</option>
                {ranks.map(rank => (
                  <option key={rank} value={rank}>{rank}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="years_of_service" className="form-label">
                Years of Service
              </label>
              <input
                type="number"
                id="years_of_service"
                name="years_of_service"
                className="form-input"
                value={formData.years_of_service}
                onChange={handleChange}
                min="0"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="beach" className="form-label">
              Beach Preference *
            </label>
            <select
              id="beach"
              name="beach"
              className="form-select"
              value={formData.beach}
              onChange={handleChange}
              disabled={loading || beachesLoading}
              required
            >
              <option value="">
                {beachesLoading ? 'Loading beaches...' : 'Select preferred beach'}
              </option>
              {beaches.map(beach => (
                <option key={beach.id} value={beach.name}>
                  {beach.name}
                </option>
              ))}
            </select>
            {beaches.length === 0 && !beachesLoading && (
              <div className="text-sm" style={{ color: '#718096', marginTop: '0.25rem' }}>
                No beaches available for selection
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password *
              </label>
              <input
                type="password"
                id="password"
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password"
                disabled={loading}
                required
              />
              <div className="text-sm" style={{ color: '#718096', marginTop: '0.25rem' }}>
                Must be at least 8 characters with uppercase, lowercase, number, and special character (@$!%*?&)
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password *
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className="form-input"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm password"
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="emt_training"
                className="form-checkbox"
                checked={formData.emt_training}
                onChange={handleChange}
                disabled={loading}
              />
              EMT Training
            </label>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-lg w-full"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-link">
          <p>
            Already have an account?{' '}
            <Link to="/login">Sign in here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register; 